import { addDays, addMinutes, differenceInCalendarDays, subMinutes } from 'date-fns';
import { millisecondsToMinutes } from 'date-fns';
import { fromZonedTime, getTimezoneOffset } from 'date-fns-tz';
import { padEnd, padStart } from 'lodash';

import { DateRange, GetDateIntervalOptions } from './types';

export { default as formatRelativeDistanceToNow } from './formatRelativeDistanceToNow';

// Converters

export const getTZOffset = (timeZone: string, date?: Date) => {
    const d = date || new Date();

    return -millisecondsToMinutes(getTimezoneOffset(timeZone, d));
};

export const getUTCDateFromTenantStringWithoutTime = (dateString: string, timeZone: string) => {
    const offsetTZ = getTZOffset(timeZone);

    const base = `${ dateString }T00:00:00`;
    const sign = offsetTZ > 0 ? '-' : '+';
    const offset = `${ padEnd(padStart((Math.abs(offsetTZ) / 60).toString(), 2, '0'), 4, '0') }`;

    return `${ base }${ sign }${ offset }`;
};

export const getTenantTimeFromUTC = (date: Date, timeZone: string) => {
    const offset = date.getTimezoneOffset();
    const offsetTZ = getTZOffset(timeZone, date);
    const diff = offsetTZ - offset;

    return subMinutes(date, diff);
};

export const getUTCFromTenantTime = (date: Date, timeZone: string) => {
    const offset = date.getTimezoneOffset();
    const offsetTZ = getTZOffset(timeZone, date);
    const diff = offsetTZ - offset;

    return addMinutes(date, diff);
};

// Periods

const getDateRangeLocal = (start: Date, end: Date): DateRange => {
    const interval: Date[] = [];
    let t = start;

    while (t.valueOf() < end.valueOf()) {
        const d = new Date(t);
        d.setHours(0, 0, 0, 0);

        interval.push(d);

        t = addDays(t, 1);
    }

    if (interval[interval.length - 1].getDate() !== end.getDate()) {
        const d = new Date(t);
        d.setHours(0, 0, 0, 0);

        interval.push(d);
    }

    if (interval.length > 1) {
        interval[interval.length - 1].setHours(23, 59, 59, 999);
    }

    return interval;
};

const getDateRangeTZ = (start: Date, end: Date, timeZone: string): DateRange => {
    const tzStart = getTenantTimeFromUTC(start, timeZone);
    const tzEnd = getTenantTimeFromUTC(end, timeZone);
    const interval = getDateRangeLocal(tzStart, tzEnd);

    const converter = (date: Date) => getUTCFromTenantTime(date, timeZone);

    return interval.map(converter);
};

export const getDateRange = (start: Date, end: Date, options?: GetDateIntervalOptions): DateRange => {
    if (start.valueOf() > end.valueOf()) {
        return [];
    }

    const timeZone = options?.timeZone;

    if (timeZone) {
        return getDateRangeTZ(start, end, timeZone);
    }

    return getDateRangeLocal(start, end);
};

// Day

/**
 * When timezone isn't provided returns 00:00:00.000 of date in provided object.
 *
 * @param { Date } day - Date object
 */
export const getStartOfDay = (day: Date): Date => {
    const scopedDate = new Date(day);

    scopedDate.setHours(0, 0, 0, 0);

    return scopedDate;
};

/**
 * Shifts provided date to timezone and returns 00:00:00.000 of resulted date
 * @param { Date } day - Date object
 * @param { string } timeZone - IANA time zone string
 */
export const getTimezoneStartOfDay = (day: Date, timeZone: string) => {
    const scopedDateTZ = getTenantTimeFromUTC(day, timeZone);
    scopedDateTZ.setHours(0, 0, 0, 0);

    return getUTCFromTenantTime(scopedDateTZ, timeZone);
};


/**
 * When timezone isn't provided returns 23:59:59.999 of date in provided object.
 *
 * @param { Date } day - Date object
 */
export const getEndOfDay = (day: Date): Date => {
    const scopedDate = new Date(day);

    scopedDate.setHours(23, 59, 59, 999);

    return scopedDate;
};

/**
 * Shifts provided date to timezone and returns 23:59:59.999 of resulted date
 * @param { Date } day - Date object
 * @param { string } timeZone - IANA time zone string
 */
export const getTimezoneEndOfDay = (day: Date, timeZone: string): Date => {
    const scopedDateTZ = getTenantTimeFromUTC(day, timeZone);
    scopedDateTZ.setHours(23, 59, 59, 999);

    return getUTCFromTenantTime(scopedDateTZ, timeZone);
};

export const differenceInCalendarDaysInTimezone = (dayA: Date, dayB: Date, timeZone: string) => {
    const a = getTenantTimeFromUTC(dayA, timeZone);
    const b = getTenantTimeFromUTC(dayB, timeZone);

    return differenceInCalendarDays(a, b);
};

export const getWeekNumber = (date: Date, timeZone: string) => {
    const tenantDate = getTenantTimeFromUTC(date, timeZone);

    if (tenantDate) {
        const d = new Date(Date.UTC(tenantDate.getFullYear(), tenantDate.getMonth(), tenantDate.getDate()));
        const dayNum = d.getUTCDay() || 7;
        d.setUTCDate(d.getUTCDate() + 4 - dayNum);
        const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));

        return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
    }

    return 0;
};
